import { convertToUTCTokens } from '~/lib/utils/date'
import { suitableCardsOnTop } from '~/lib/utils/filters'

interface EligibleWeight {
  scholarshipId: number;
  weight: number;
}

export const SORT_EXPIRATION = 'deadline'
export const SORT_AMOUNT = 'amount'
export const SORT_REQ_COUNT = 'reqcount'
export const SORT_APP_COUNT = 'appcount'
export const SORT_DEFAULT = 'reqcount'
export const SORT_RECENT_SUBMIT = 'recentsubmit'
export const SORT_STATUS_UPDATE = 'statusupd'
export const SORT_MAGIC = 'magic'
export const SORT_NEW = 'newest'

type Order = 'asc' | 'desc'

export const ORDER_ASC = 'asc'
export const ORDER_DESC = 'desc'

export const sortByReqCount = (list: Scholarship.General[], order: Order) => {
  return list.sort((a, b) => {
    return order === ORDER_ASC
      ? a.requirementTotalCount - b.requirementTotalCount
      : b.requirementTotalCount - a.requirementTotalCount
  })
}
export const sortByConfidenceScore = (list: Scholarship.General[], order: Order) => {
  return list.sort((a, b) => {
    return order === ORDER_ASC
      ? Number(a.confidenceScore) - Number(b.confidenceScore)
      : Number(b.confidenceScore) - Number(a.confidenceScore)
  })
}

export const sortByAmount = (scholarships: Scholarship.General[], order: Order) => {
  return scholarships.sort((a, b) => {
    return order === ORDER_ASC
      ? a.amount - b.amount
      : b.amount - a.amount
  })
}

export const sortByExpiration = (scholarships: Scholarship.General[], order: Order) => {
  return scholarships.sort((a, b) => {
    if (a.expirationDate && b.expirationDate) {
      const dateA = Date.parse(a.expirationDate)
      const dateB = Date.parse(b.expirationDate)

      return order === ORDER_DESC ? dateB - dateA : dateA - dateB
    }

    return 0
  })
}

export const sortByDate = (list: Scholarship.GeneralSent[]) => {
  return list.sort((a, b) => {
    // @ts-ignore
    return Date.UTC(...convertToUTCTokens(b.dateApplied)) - Date.UTC(...convertToUTCTokens(a.dateApplied))
  })
}

function getCapState(item: Scholarship.General): 'banner' | 'red' | 'orange' | 'green' | null {
  if (!item.applicationCap || item.applicationCap <= 0) { return null }
  const spotsLeft = Math.max(0, item.applicationCap - item.applicationCount)
  if (spotsLeft <= 10 && spotsLeft > 0) { return 'banner' }
  const freePercent = (spotsLeft / item.applicationCap) * 100
  if (freePercent < 20) { return 'red' }
  if (freePercent <= 50) { return 'orange' }
  return 'green'
}

export const sortDefaultMatches = (list: Scholarship.General[], isFlagEnabled = false) => {
  list = sortByReqCount(list, ORDER_ASC)
  list = sortByAmount(list, ORDER_DESC)
  list = sortByExpiration(list, ORDER_ASC)
  list = sortByConfidenceScore(list, ORDER_DESC)
  list = suitableCardsOnTop(list, item => item.requirementTotalCount === 0)
  list = suitableCardsOnTop(list, item => Number(item.confidenceScore) >= 76)
  list = suitableCardsOnTop(list, item => !!item.isAutomatic)
  list = suitableCardsOnTop(list, item => !!item.isSponsored)
  list = suitableCardsOnTop(list, item => item.scholarshipType === 'coreg')

  if (isFlagEnabled) {
    list = suitableCardsOnTop(list, item => getCapState(item) === 'green')
    list = suitableCardsOnTop(list, item => getCapState(item) === 'orange')
    list = suitableCardsOnTop(list, item => getCapState(item) === 'red')
    list = suitableCardsOnTop(list, item => getCapState(item) === 'banner')
  }

  return list
}

type SortBy = <T>(list: T[], order: Order, propertyName: keyof T) => T[]
export const sortBy: SortBy = (list, order, propertyName) => {
  list.sort((a, b) => {
    if (order === ORDER_ASC) { return Number(a[propertyName]) - Number(b[propertyName]) }
    if (order === ORDER_DESC) { return Number(b[propertyName]) - Number(a[propertyName]) }
    return 0
  })

  return list
}

export const sortDefault = (list: Scholarship.General[], order: Order = ORDER_ASC) => {
  return sortByReqCount(list, order)
}

export const sortByAppCount = (list: Scholarship.General[], order: Order) => {
  return list.sort((a, b) => {
    if (order === ORDER_ASC) { return a.applicationCount - b.applicationCount }
    if (order === ORDER_DESC) { return b.applicationCount - a.applicationCount }
    return 0
  })
}
export const sortByNewest = (list: Scholarship.General[], order: Order) => {
  return list.sort((a, b) => {
    return order === ORDER_ASC
      ? a.scholarshipId - b.scholarshipId
      : b.scholarshipId - a.scholarshipId
  })
}
export const sortByRecentSubmit = (scholarships: Scholarship.GeneralSent[], order: Order) => {
  return scholarships.sort((a, b) => {
    if (a.dateAppliedWithoutTimezone && b.dateAppliedWithoutTimezone) {
      const dateA = Date.parse(a.dateAppliedWithoutTimezone)
      const dateB = Date.parse(b.dateAppliedWithoutTimezone)

      return order === ORDER_DESC ? dateB - dateA : dateA - dateB
    }

    return 0
  })
}

export const sortByStatusUpdated = (scholarships: Scholarship.GeneralSent[], order: Order) => {
  // Filter scholarships with null externalAggregateStatusUpdatedAt
  const scholarshipsWithStatusUpdated = scholarships.filter(scholarship => scholarship.externalAggregateStatusUpdatedAt !== null)

  // Sort scholarships with non-null externalAggregateStatusUpdatedAt
  const sortedByStatusUpdated = scholarshipsWithStatusUpdated.sort((a, b) => {
    const dateA = Date.parse(a.externalAggregateStatusUpdatedAt)
    const dateB = Date.parse(b.externalAggregateStatusUpdatedAt)
    return order === ORDER_DESC ? dateB - dateA : dateA - dateB
  })

  // Sort scholarships with null externalAggregateStatusUpdatedAt by dateApplied
  const scholarshipsWithoutStatusUpdated = scholarships.filter(scholarship => scholarship.externalAggregateStatusUpdatedAt === null)
  const sortedByDateApplied = scholarshipsWithoutStatusUpdated.sort((a, b) => {
    const dateA = Date.parse(a.dateApplied)
    const dateB = Date.parse(b.dateApplied)
    return order === ORDER_DESC ? dateB - dateA : dateA - dateB
  })
  // Combine the sorted arrays
  return sortedByStatusUpdated.concat(sortedByDateApplied)
}

export const sortByEligibleWeight = (...args) => {
  const list: Scholarship.General[] = args[0]
  const opts: EligibleWeight[] = args[2]

  if (!opts) { return list }
  const scholarships = list.reduce((acc, s) => {
    acc[s.scholarshipId] = s; return acc
  }, {} as { [key: string]: Scholarship.General })
  return opts.map(s => scholarships[s.scholarshipId]).filter(s => !!s)
}

export const sortMethods = {
  default: {
    [SORT_MAGIC]: sortByEligibleWeight,
    [SORT_EXPIRATION]: sortByExpiration,
    [SORT_AMOUNT]: sortByAmount,
    [SORT_REQ_COUNT]: sortByReqCount,
    [SORT_APP_COUNT]: sortByAppCount,
    [SORT_DEFAULT]: sortDefault,
    [SORT_NEW]: sortByNewest
  },
  submitted: {
    [SORT_RECENT_SUBMIT]: sortByRecentSubmit,
    [SORT_STATUS_UPDATE]: sortByStatusUpdated,
    [SORT_DEFAULT]: sortDefault
  }
}
